import { TypedMoney } from "@commercetools/platform-sdk";
import { css } from "@emotion/react";
import { showPrice } from "@swo/shared";
import {
  CustomerOrderPayAgainDetailsResponse,
  SOPaymentMethods,
  SwarovskiOptikLocaleFrontend,
  type CheckoutTranslation,
  type PurchaseConfirmationPageTranslation,
  type TranslatedValue,
} from "@swo/storefront";
import cn from "classnames";
import * as E from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { SwarovskiButton } from "../../../components/_shared-components/ui-elements/SwarovskiButton";
import { UserAuthToken } from "../../../domain/auth/user-auth/auth-client";
import { StorefrontCheckoutClient } from "../../../domain/swarovski-optik/order/place-order";
import { type Translated } from "../../../domain/translations/translations";
import { useFetched } from "../../../hooks";
import { useStore } from "../../../store/hooks/use-store";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { CheckoutPaymentMethod } from "../checkout-page-layout/CheckoutComponent/checkout-steps/03-payment-method/CheckoutPaymentMethod";
import { useOrderPlacedHandler } from "../checkout-page-layout/CheckoutComponent/context/use-order-placed-handler";
import { CheckoutHeadline } from "../checkout-page-layout/CheckoutComponent/shared/CheckoutHeadline";
import { CheckoutPaymentMethodSelectionState } from "../checkout-page-layout/CheckoutComponent/state/payment-methods";
import { toPlaceOrderPaymentMethodData } from "../checkout-page-layout/CheckoutComponent/state/to-place-order-payment-method-data";
import { OrderStatusPage } from "./order-status-page";
import { PaymentStatusPageWithPaymentId } from "./pages/payment-status-page/payment-status-page";
import { loadingScreen } from "./shared-components/loading-screen";
import { OrderHeadline } from "./shared-components/order-headline";
import { Wrapper } from "./shared-components/wrapper";
import { PaymentStep, PaymentStepCtxReq, UsePaymentStepParams } from "./use-payment-step";

export const PurchaseConfirmationPageContent: ReactTFC<{
  paymentStep: PaymentStep;
  updatePaymentStep: (ctx: UsePaymentStepParams) => void;
  homepageLink: string;
  locale: SwarovskiOptikLocaleFrontend;
  t: Translated<PurchaseConfirmationPageTranslation>;
  linkToPurchaseConfirmation: string;
  authToken: UserAuthToken;
  repeatPaymentLink: {
    pathname: string;
    search: string;
  };
  orderPageLink: {
    pathname: string;
    search: string;
  };
  contentAreaComponents: React.JSX.Element | null;
}> = ({ t, ...props }) => {
  const repeatPaymentLabel = t("repeatPaymentLabel");
  const orderNotFoundLabel = t("orderNotFoundLabel");
  const orderLabel = t("orderLabel");
  const paymentsLabel = t("paymentsLabel");
  const paymentStatusLabel = t("paymentStatusLabel");
  const paymentLabel = t("paymentLabel");

  const { paymentStep, homepageLink, updatePaymentStep, locale } = props;
  const localeCode = locale.code;
  const { tag, context } = paymentStep;
  const accessToken = props.authToken.access_token;

  const [isLoading, setLoading] = useState(false);
  const isPending = useRef(false);

  const storefrontCheckoutClient = new StorefrontCheckoutClient(accessToken, localeCode);

  useEffect(() => {
    const { tag, context } = paymentStep;

    if (tag === "finalize") {
      const { context, redirectResult } = paymentStep;

      isPending.current = true;
      void storefrontCheckoutClient
        .finalizePaymentWithRedirectResult({
          paymentId: context.paymentId,
          orderId: context.orderId,
          redirectResult: redirectResult,
        })
        .finally(() => {
          isPending.current = false;
          onFinally(context);
        });
    }
  }, [paymentStep]);

  const onError = (context: PaymentStepCtxReq) => {
    setLoading(true);
    void storefrontCheckoutClient
      .finalize3DSecure2Payment({
        ...context,
        threeDSResult: "",
      })
      .finally(() => onFinally(context));
  };

  const onFinally = useCallback((ctx: { paymentId?: string; orderId: string }) => {
    setLoading(false);
    updatePaymentStep({
      orderId: ctx.orderId,
      paymentId: ctx.paymentId,
      redirectResult: undefined,
      payAgain: undefined,
    });
  }, []);

  const on3DSecure2Result = (context: PaymentStepCtxReq) => (threeDSResult: string) => {
    void storefrontCheckoutClient
      .finalize3DSecure2Payment({
        ...context,
        threeDSResult: threeDSResult,
      })
      .finally(() => onFinally(context));
  };

  const onPaypalAdditionalData =
    (context: PaymentStepCtxReq) => (additionalDetails: { details: unknown; paymentData: string }) => {
      void storefrontCheckoutClient
        .finalizePaypal({
          ...context,
          additionalDetails,
        })
        .finally(() => onFinally(context));
    };

  const onApplePayPaymentAuthorised = (context: PaymentStepCtxReq) => (token: string) => {
    void storefrontCheckoutClient
      .makeApplePayPayment(context, token, {
        linkToPurchaseConfirmation: props.linkToPurchaseConfirmation,
      })
      .finally(() => onFinally(context));
  };

  const onPaymentCancelled = (context: PaymentStepCtxReq) => () => {
    void storefrontCheckoutClient
      .makeApplePayPayment(context, "payment-cancelled", {
        linkToPurchaseConfirmation: props.linkToPurchaseConfirmation,
      })
      .finally(() => onFinally(context));
  };

  const { locationOrigin } = useStore().state;

  const onOrderPlaced = useOrderPlacedHandler(props.linkToPurchaseConfirmation);

  const onPayAgain = (paymentMethod: NonNullable<CheckoutPaymentMethodSelectionState>): Promise<void> => {
    const paymentData = toPlaceOrderPaymentMethodData(paymentMethod);

    const returnUrl = new URL(props.linkToPurchaseConfirmation, locationOrigin);

    return storefrontCheckoutClient.payAgain(context.orderId, returnUrl, paymentData).then(response => {
      return pipe(
        response,
        E.foldW(
          err => {
            console.log(err);
          },
          data => {
            console.log("onOrderPlaced", data);

            return onOrderPlaced(data);
          },
        ),
      );
    });
  };

  if (isLoading) {
    return loadingScreen;
  }

  switch (tag) {
    case "result": {
      return (
        <PaymentStatusPageWithPaymentId
          key={paymentStep.key}
          homepageLink={homepageLink}
          paymentId={context.paymentId}
          orderId={context.orderId}
          t={t}
          onPaypalAdditionalData={onPaypalAdditionalData}
          on3DSecure2Result={on3DSecure2Result}
          onApplePayPaymentAuthorised={onApplePayPaymentAuthorised}
          onPaymentCancelled={onPaymentCancelled}
          onError={onError}
          LoadingScreen={loadingScreen}
          fetchOrderPaymentStatus={(abortSignal: AbortSignal) =>
            storefrontCheckoutClient.getOrderPaymentStatus(context, localeCode, abortSignal)
          }
          contentAreaComponents={props.contentAreaComponents}
        />
      );
    }

    case "order": {
      return (
        <OrderStatusPage
          homepageLink={homepageLink}
          orderId={context.orderId}
          LoadingScreen={loadingScreen}
          fetchOrderStatus={() => storefrontCheckoutClient.getOrderStatus({ orderId: context.orderId })}
          repeatPayment={{
            link: props.repeatPaymentLink,
            buttonLabel: repeatPaymentLabel,
          }}
          translations={{
            orderNotFoundLabel,
            orderLabel,
            paymentLabel,
            paymentStatusLabel,
            paymentsLabel,
          }}
        />
      );
    }

    case "pay-again": {
      return (
        <PayAgainForAnOrder
          orderId={context.orderId}
          getOrderPayAgainDetails={() =>
            storefrontCheckoutClient.getOrderPayAgainDetails({ orderId: context.orderId }, localeCode)
          }
          locale={props.locale}
          onPayAgain={onPayAgain}
          orderPageLink={props.orderPageLink}
          translations={{
            orderLabel,
          }}
        />
      );
    }

    case "finalize": {
      return loadingScreen;
    }

    default: {
      const _: never = tag;

      return _;
    }
  }
};

const PayAgainForAnOrder: ReactTFC<{
  orderId: string;
  getOrderPayAgainDetails: () => Promise<CustomerOrderPayAgainDetailsResponse>;
  locale: SwarovskiOptikLocaleFrontend;
  onPayAgain: (paymentMethod: NonNullable<CheckoutPaymentMethodSelectionState>) => Promise<void>;
  orderPageLink: {
    pathname: string;
    search: string;
  };
  translations: {
    orderLabel: TranslatedValue;
  };
}> = props => {
  const fetched = useFetched(props.getOrderPayAgainDetails, { dataKey: props.orderId });

  if (fetched.__tag !== "loaded") {
    return <Wrapper className={cn("expand fill-screen")} />;
  }

  const { data } = fetched;

  switch (data.tag) {
    case "not-possible": {
      return (
        <Wrapper className={cn("expand fill-screen")}>
          <ContentContainer>
            <div>
              <CheckoutHeadline>Not possible</CheckoutHeadline>
            </div>
          </ContentContainer>
        </Wrapper>
      );
    }

    case "possible": {
      const { availablePaymentMethods, translations, amount } = data.data;

      return (
        <Wrapper className={cn("expand fill-screen")}>
          <ContentContainer>
            <div>
              <OrderHeadline orderLabel={props.translations.orderLabel} orderNumber={data.order.orderNumber} />
            </div>
          </ContentContainer>
          <ContentContainer>
            <div
              css={css`
                max-width: 600px;
                margin: 0 auto;
              `}
            >
              <RetryPayment
                availablePaymentMethods={availablePaymentMethods}
                translations={translations}
                amount={amount}
                locale={props.locale}
                order={data.order}
                onPayAgain={props.onPayAgain}
                backLink={props.orderPageLink}
              />
            </div>
          </ContentContainer>
        </Wrapper>
      );
    }
  }
};

const RetryPayment: ReactTFC<{
  availablePaymentMethods: SOPaymentMethods["availablePaymentMethods"];
  translations: CheckoutTranslation;
  amount: TypedMoney;
  locale: SwarovskiOptikLocaleFrontend;
  order: {
    id: string;
    orderNumber: string;
  };
  backLink: {
    pathname: string;
    search: string;
  };
  onPayAgain: (paymentMethod: NonNullable<CheckoutPaymentMethodSelectionState>) => Promise<void>;
}> = props => {
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<CheckoutPaymentMethodSelectionState>(null);

  const { clientKey, environment } = useStore().state.config.adyen;

  const orderValue = showPrice(props.amount, props.locale);

  return (
    <>
      <CheckoutPaymentMethod
        disabled={false}
        currentCartPaymentMethod={currentPaymentMethod}
        onCurrentCartPaymentMethodChanged={value => setCurrentPaymentMethod(value)}
        options={props.availablePaymentMethods}
        translations={{
          payPal: props.translations["paymentMethods.paypal"],
          creditCard: props.translations["paymentMethods.creditCard"],
          creditCardSave: props.translations["paymentMethods.creditCard.save"],
          creditCardEdit: props.translations["paymentMethods.creditCard.edit"],
          creditCardClose: props.translations["paymentMethods.creditCard.close"],
          creditCardNumber: props.translations["paymentMethods.creditCard.number"],
          creditCardExpiryDate: props.translations["paymentMethods.creditCard.expiryDate"],
          creditCardSecurityCode: props.translations["paymentMethods.creditCard.securityCode"],
          zeroAmountPayment: props.translations["paymentMethods.zeroAmountPayment.message"],
        }}
        clientKey={clientKey}
        environment={environment}
      />

      <div
        css={css`
          margin-top: 40px;
          display: flex;
          justify-content: space-between;
          align-items: baseline;
        `}
      >
        <SwarovskiButton variant={"plain-underline"} as={"a"} isInternal={true} link={props.backLink}>
          Back
        </SwarovskiButton>

        <SwarovskiButton
          variant={"green"}
          as={"button"}
          disabled={!currentPaymentMethod}
          onClickWithPromise={currentPaymentMethod ? () => props.onPayAgain(currentPaymentMethod) : undefined}
        >
          Pay {orderValue}
        </SwarovskiButton>
      </div>
    </>
  );
};
